import React from 'react';
import classNames from 'classnames';

// Section components
import SectionArticle from './SectionArticle';
import SectionCarousel from './SectionCarousel';
import SectionColumns from './SectionColumns';
import SectionFeatures from './SectionFeatures';
import SectionHero from './SectionHero';
import SectionWhyUs from './SectionWhyUs';
import SectionListings from './SectionListings';
import SectionCta from './SectionCta';
import SectionBenefits from './SectionBenefits';
import SectionReviews from './SectionReviews';
import SectionInstagram from './SectionInstagram';
import SectionHighlights from './SectionHighlights';
import SectionFeatureColumns from './SectionFeatureColumns';
import SectionMethodColumns from './SectionMethodColumns';
import SectionArticleWithColumns from './SectionArticleWithColumns';
import SectionAlertForm from './SectionAlertForm/SectionAlertForm';

// Styles
// Note: these contain
// - shared classes that are passed as defaultClasses
// - dark theme overrides
// TODO: alternatively, we could consider more in-place way of theming components
import css from './SectionBuilder.module.css';
import SectionFooter from './SectionFooter';

// These are shared classes.
// Use these to have consistent styles between different section components
// E.g. share the same title styles
const DEFAULT_CLASSES = {
  sectionDetails: css.sectionDetails,
  title: css.title,
  description: css.description,
  ctaButton: css.ctaButton,
  blockContainer: css.blockContainer,
  defaultLink: css.defaultLink,
};

/////////////////////////////////////////////
// Mapping of section types and components //
/////////////////////////////////////////////

const defaultSectionComponents = {
  article: { component: SectionArticle },
  carousel: { component: SectionCarousel },
  columns: { component: SectionColumns },
  features: { component: SectionFeatures },
  footer: { component: SectionFooter },
  hero: { component: SectionHero },
  listings: { component: SectionListings },
  // Custom sections
  'why-us': { component: SectionWhyUs },
  'featured-listings': { component: SectionListings },
  cta: { component: SectionCta },
  benefits: { component: SectionBenefits },
  reviews: { component: SectionReviews },
  instagram: { component: SectionInstagram },
  highlights: { component: SectionHighlights },
  'feature-columns': { component: SectionFeatureColumns },
  'method-columns': { component: SectionMethodColumns },
  'article-with-columns': { component: SectionArticleWithColumns },
};

//////////////////////
// Section builder //
//////////////////////

/**
 * @typedef {Object} FieldOption
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * @typedef {Object} BlockOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionConfig
 * @property {string} sectionId
 * @property {string} sectionName
 * @property {('article' | 'carousel' | 'columns' | 'features' | 'hero' | 'listings')} sectionType
 */

/**
 * Build section elements from given section config array.
 *
 * @component
 * @param {Object} props
 * @param {Array<SectionConfig>} props.sections
 * @param {Object} props.options
 * @param {Object<string,FieldOption>} props.options.fieldComponents
 * @param {Object<string,BlockOption>} props.options.blockComponents
 * @param {Object<string,SectionOption>} props.options.sectionComponents
 * @param {boolean} props.options.isInsideContainer
 * @returns {JSX.Element} element containing array of sections according from given config array.
 */
// On the "personalised_offers" CMS page we embed the "create new alert" form
// (the same one rendered on /personalised_offers) right after this many sections.
const ALERT_FORM_PAGE_ID = 'personalised_offers';
const ALERT_FORM_AFTER_SECTION_INDEX = 1; // i.e. after the first two sections

const SectionBuilder = props => {
  const { sections = [], options, pageId } = props;
  const { sectionComponents = {}, isInsideContainer, ...otherOption } = options || {};

  // If there's no sections, we can't render the correct section component
  if (!sections || sections.length === 0) {
    return null;
  }

  // Selection of Section components.
  // Note: the lookup must use the section id as it was configured in Console
  // (not the deduplicated one below), so that several sections can share an
  // anchor id and still all get the custom template it maps to.
  const components = { ...defaultSectionComponents, ...sectionComponents };
  const getComponent = (sectionType, configuredSectionId) => {
    const config = !!components[configuredSectionId]
      ? components[configuredSectionId]
      : components[sectionType];
    return config?.component;
  };

  // Generate unique ids for sections if operator has managed to create duplicates
  // E.g. "foobar", "foobar1", and "foobar2"
  const sectionIds = [];
  const getUniqueSectionId = (sectionId, index) => {
    const candidate = sectionId || `section-${index + 1}`;
    if (sectionIds.includes(candidate)) {
      let sequentialCandidate = `${candidate}1`;
      for (let i = 2; sectionIds.includes(sequentialCandidate); i++) {
        sequentialCandidate = `${candidate}${i}`;
      }
      return getUniqueSectionId(sequentialCandidate, index);
    } else {
      sectionIds.push(candidate);
      return candidate;
    }
  };

  // Resolve all section ids
  const sectionsWithResolvedIds = sections.map((section, index) => ({
    ...section,
    sectionId: getUniqueSectionId(section.sectionId, index),
  }));

  return (
    <>
      {sectionsWithResolvedIds.map((section, index) => {
        // The id from Console. Unlike section.sectionId, it is not made unique,
        // so it still matches the template mapping for duplicated anchor ids.
        const configuredSectionId = sections[index].sectionId;
        const Section = getComponent(section.sectionType, configuredSectionId);
        // If the default "dark" theme should be applied (when text color is white).
        // By default, this information is stored to customAppearance field
        const isDarkTheme =
          section?.appearance?.fieldType === 'customAppearance' &&
          section?.appearance?.textColor === 'white';
        const classes = classNames({
          [css.darkTheme]: isDarkTheme,
          [css.sectionWhyUs]: configuredSectionId === 'why-us',
        });
        const sectionId = section.sectionId;

        // Embed the "create new alert" form after the configured section
        // on the personalised offers page.
        const shouldRenderAlertForm =
          pageId === ALERT_FORM_PAGE_ID && index === ALERT_FORM_AFTER_SECTION_INDEX;
        const alertFormMaybe = shouldRenderAlertForm ? (
          <SectionAlertForm
            key="personalised-offers-alert-form"
            sectionId="personalised-offers-alert-form"
            className={classes}
            defaultClasses={DEFAULT_CLASSES}
            options={{ ...otherOption, defaultClasses: DEFAULT_CLASSES }}
          />
        ) : null;

        if (Section) {
          return (
            <React.Fragment key={`${sectionId}_i${index}`}>
              <Section
                className={classes}
                defaultClasses={DEFAULT_CLASSES}
                isInsideContainer={isInsideContainer}
                options={{ ...otherOption, defaultClasses: DEFAULT_CLASSES }}
                {...section}
                sectionId={sectionId}
                pageId={pageId}
                allSections={sectionsWithResolvedIds}
              />
              {alertFormMaybe}
            </React.Fragment>
          );
        } else {
          // If the section type is unknown, the app can't know what to render
          console.warn(
            `Unknown section type (${section.sectionType}) detected using sectionName (${section.sectionName}).`
          );
          return alertFormMaybe ? (
            <React.Fragment key={`${sectionId}_i${index}`}>{alertFormMaybe}</React.Fragment>
          ) : null;
        }
      })}
    </>
  );
};

export default SectionBuilder;
